import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Renders a paginated list of bookings (or any homogeneous item) with a
 * "See More → / Show Less" toggle. Initial page shows `limit` items
 * (default 6); the rest stay rendered but collapsed until the user expands.
 *
 * No navigation. No data loss. Sort + pagination handled here, render-prop
 * style so each dashboard can keep its own card markup.
 *
 * Usage:
 *   <ExpandableBookingList
 *     items={bookings}
 *     limit={6}
 *     sortBy={(a, b) => new Date(b.created_at) - new Date(a.created_at)}
 *     emptyState={<div>...</div>}
 *     renderItem={(b) => <div key={b.booking_id}>...</div>}
 *     testid="provider-bookings"
 *   />
 */
export default function ExpandableBookingList({
  items,
  limit = 6,
  sortBy,
  renderItem,
  emptyState,
  testid,
  className = "space-y-3",
}) {
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : [];
    if (typeof sortBy === "function") arr.sort(sortBy);
    return arr;
  }, [items, sortBy]);

  if (sorted.length === 0) {
    return emptyState || null;
  }

  const visible = expanded ? sorted : sorted.slice(0, limit);
  const hasMore = sorted.length > limit;

  return (
    <>
      <div className={className} data-testid={testid}>
        {visible.map((it) => renderItem(it))}
      </div>
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            data-testid={`${testid}-toggle`}
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#064E3B]/80 px-4 py-2 rounded-full border border-[#064E3B]/20 hover:bg-[#064E3B]/5 transition"
          >
            {expanded ? (
              <>
                <ChevronUp size={14}/> Show less
              </>
            ) : (
              <>
                See more ({sorted.length - limit} more) <ChevronDown size={14}/>
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
}
